/** Phase 2E-camp-2 — typed assignee helpers. Kept in its own module
 *  so vitest can mock at this layer without touching @/lib/api (the
 *  axios instance there is wired up to auth-failure handlers that
 *  blow up under a naive vi.mock). */
import { ApiPaths, api } from "@/lib/api";


export type Assignee = {
  id: string;
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  assigned_by_email: string | null;
  assigned_at: string;
  /** False = paused: still on the campaign, no longer counted for
   *  capacity and no longer offered slots. */
  is_bookable: boolean;
};


export async function listAssignees(campaignId: string): Promise<Assignee[]> {
  const res = await api.get<Assignee[]>(
    ApiPaths.campaignAssignees(campaignId),
  );
  return Array.isArray(res.data) ? res.data : [];
}


export async function addAssignees(
  campaignId: string,
  userIds: number[],
): Promise<Assignee[]> {
  const res = await api.post<Assignee[]>(
    ApiPaths.campaignAssignees(campaignId),
    { user_ids: userIds },
  );
  return res.data;
}


/** What an operator still holds on a campaign.
 *
 *  `pending` is what a removal would have to move; `completed_sessions`
 *  is what it never will — a performed inspection keeps the operator who
 *  performed it, because its recordings, GPS and audit chain say so.
 */
export type AssigneeWork = {
  user_id: number;
  assigned: boolean;
  is_bookable: boolean | null;
  pending: { sessions: number; memberships: number; total: number };
  completed_sessions: number;
};

export async function getAssigneeWork(
  campaignId: string,
  userId: number,
): Promise<AssigneeWork> {
  const res = await api.get<AssigneeWork>(
    ApiPaths.campaignAssignee(campaignId, userId),
  );
  return res.data;
}

/** Pause or resume an operator on a campaign.
 *
 *  Paused, they keep the campaign and everything they have already
 *  done, but stop counting toward capacity and stop being offered
 *  slots. It is the answer to "assigned, but not taking calls yet",
 *  which removal answers badly.
 */
export async function setAssigneeBookable(
  campaignId: string,
  userId: number,
  isBookable: boolean,
): Promise<void> {
  await api.patch(ApiPaths.campaignAssignee(campaignId, userId), {
    is_bookable: isBookable,
  });
}

/** Remove an operator, handing their unrun work to `reassignTo`.
 *
 *  Without a target the API answers 409 when anything is outstanding —
 *  deliberately, so the UI asks rather than orphaning sessions that
 *  would keep pointing at somebody no longer on the campaign.
 */
export async function removeAssignee(
  campaignId: string,
  userId: number,
  reassignTo?: number,
): Promise<void> {
  const url = ApiPaths.campaignAssignee(campaignId, userId);
  await api.delete(reassignTo ? `${url}?reassign_to=${reassignTo}` : url);
}
